import React from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { gesturesKeys } from 'components/Tiles/Student/Gestures'
import { gestureEventEmitter, gestureKeys } from 'helpers/events'
import { isEmpty } from 'helpers/lodash'
import { isString } from 'helpers/miscellaneous'
import { sendGesture, sendRevertGesture } from 'services/webSockets/gestures'
import { dispatchAddGesture, dispatchRemoveGesture } from 'store/dispatcher'
import { _staticGestures } from 'store/selectors/Gestures'

const useGesture = (isLocal, identity) => {
    const staticGestures = useSelector(_staticGestures)

    const [gestureData, setGestureData] = React.useState({
        show: false,
        gestureKey: null,
        gestureType: null,
        gesturePK: null,
    })

    React.useEffect(() => {
        setGestureData(state => {
            if (!state.show && !isEmpty(staticGestures)) {
                const userGesture = Object.values(staticGestures).find(
                    gesture => gesture.from_user === identity.uuid
                )

                if (!isEmpty(userGesture)) {
                    const {
                        gesture: gestureKey,
                        gesture_type: gestureType,
                        gesture_pk: gesturePK,
                    } = userGesture

                    return {
                        ...state,
                        show: true,
                        gestureKey,
                        gestureType,
                        gesturePK,
                    }
                }
            }

            return state
        })
    }, [staticGestures])

    React.useEffect(() => {
        let timeoutHandler

        const handler = ({
            gestureKey,
            gestureType,
            autoHide,
            sender,
            gesturePK,
        }) => {
            // break early if its not local user and sender and identity uuid is not same
            if (!isLocal && sender !== identity.uuid) return

            const isGesturePKAvailable = Number.isInteger(gesturePK)

            if (
                (isLocal && (!sender || sender === identity.uuid)) ||
                (!isLocal && sender === identity.uuid)
            ) {
                const data = {
                    show: true,
                    gestureKey,
                    gestureType: !autoHide ? gestureType : null,
                    ...(isGesturePKAvailable ? { gesturePK } : null),
                }
                setGestureData(data)

                if (autoHide) {
                    timeoutHandler = setTimeout(() => {
                        setGestureData(state => {
                            if (
                                state.show &&
                                isString(state.gestureKey) &&
                                state.gestureKey === gesturesKeys.raiseHand
                            )
                                return state

                            return {
                                ...state,
                                show: false,
                            }
                        })
                    }, 3000)
                }
            }

            if (isGesturePKAvailable && gestureKey === gesturesKeys.raiseHand) {
                dispatchAddGesture({
                    gesture: gestureKey,
                    gesture_type: gestureType,
                    from_user: sender,
                    gesture_pk: gesturePK,
                })
            }

            if (isLocal && !sender) {
                // broadcast websocket message
                sendGesture(gestureKey, gestureType)
            }
        }

        gestureEventEmitter.addListener(gestureKeys.GESTURE_MSG, handler)

        return () => {
            gestureEventEmitter.removeListener(gestureKeys.GESTURE_MSG, handler)
            if (timeoutHandler) {
                clearTimeout(timeoutHandler)
            }
        }
    }, [])

    React.useEffect(() => {
        const revertHandler = ({ gesturePK }) => {
            if (Number.isInteger(gesturePK)) {
                // for remote user
                const gesture = Object.values(staticGestures).find(
                    item => item.gesture_pk === gesturePK
                )

                if (isEmpty(gesture)) return

                const { gesture_pk } = gesture

                if (gesture.from_user === identity.uuid) {
                    if (isLocal) {
                        // broadcast websocket message
                        sendRevertGesture(gesture_pk)
                    }

                    setGestureData({
                        show: false,
                        gestureKey: gesture.gesture_key,
                        gestureType: gesture.gesture_type,
                    })
                }
                // remove from store
                dispatchRemoveGesture({ gesture_pk })
            }
        }

        gestureEventEmitter.addListener(
            gestureKeys.REVERT_GESTURE_MSG,
            revertHandler
        )

        return () => {
            gestureEventEmitter.removeListener(
                gestureKeys.REVERT_GESTURE_MSG,
                revertHandler
            )
        }
    }, [staticGestures])

    return gestureData
}

export { useGesture }
